import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Modal } from 'antd';

import RecordBadge from '../common/RecordBadge';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { detachFromParent } from '../../services/subCollections';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';

/**
 * A card the refusal names, as its record badge when the card is known here
 * (platform standards §1: a card mentioned from elsewhere is a badge), else
 * the plain words "a card" — the refusal carries ids, and a card this page
 * has not loaded has no name to show.
 */
const CardMention = ( { id, cardsById, fallback = 'a card' } ) => {
	const card = cardsById[ id ];
	if ( ! card ) return fallback;
	return <RecordBadge kind='card' record={card} title={card.name || 'Untitled card'} size='xs' />;
};

/**
 * What stops a detach (409 `inherited_cards_in_use`): the items still made
 * from the parent's cards, own cards that extend one or use one of its
 * element sets (`setCardIds` — detached, their element names would stay
 * here, read by people who no longer read the parent), grants on one or to a
 * parent's group, dynamic groups reading one — each named, with a door.
 */
const DetachBlockers = ( { detail, cardsById = {}, collectionId, parentName } ) => {
	if ( ! detail || detail.code !== 'inherited_cards_in_use' ) return null;
	const parent = parentName || 'the parent collection';
	const rows = [];
	( Array.isArray( detail.cards ) ? detail.cards : [] ).forEach( card => {
		const id = normalizeId( card?._id );
		rows.push( <li key={`card-${ id }`}>{ formatCount( card?.itemCount, 'item' ) || 'Items' } made from <CardMention id={id} cardsById={cardsById} /></li> );
	} );
	( Array.isArray( detail.extendingCardIds ) ? detail.extendingCardIds : [] ).forEach( raw => {
		const id = normalizeId( raw );
		rows.push( <li key={`ext-${ id }`}><CardMention id={id} cardsById={cardsById} fallback='A card' /> extends a card that belongs to { parent }</li> );
	} );
	( Array.isArray( detail.setCardIds ) ? detail.setCardIds : [] ).forEach( raw => {
		const id = normalizeId( raw );
		rows.push( <li key={`set-${ id }`}><CardMention id={id} cardsById={cardsById} fallback='A card' /> uses an element set that belongs to { parent } — take the set off the card first</li> );
	} );
	const grants = Array.isArray( detail.grantIds ) ? detail.grantIds.length : 0;
	if ( grants ) rows.push( <li key='grants'>{ formatCount( grants, 'grant' ) } on a parent&rsquo;s card or to a parent&rsquo;s group — see <Link to={`/collections/${ collectionId }/form?tab=sharing`}>Sharing</Link></li> );
	const groups = Array.isArray( detail.groupIds ) ? detail.groupIds.length : 0;
	if ( groups ) rows.push( <li key='groups'>{ formatCount( groups, 'dynamic group' ) } whose rule reads a parent&rsquo;s card</li> );
	return rows.length ? <ul className='danger-zone__blockers'>{rows}</ul> : null;
};

/**
 * DETACH FROM {PARENT}? (docs/plans/sub-collections.md §5.8) — the one
 * confirm behind both doors: the sub-collection's own Danger zone, and the
 * row ⋯ on its parent's Sub-collections tab. `collection` is the
 * sub-collection (a document or a `subCollectionsOf` summary — its id and
 * name are all this reads), `parent` its parent's summary. A refusal stays
 * in the dialog with what still depends on the parent; a detach that goes
 * through closes it, then calls `onDetached`.
 */
export default function DetachSubCollectionModal( { open, collection, parent, onClose, onDetached } ) {
	const { message } = App.useApp();
	const [ busy, setBusy ] = useState( false );
	const [ refusal, setRefusal ] = useState( null );
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById || {};
	const collectionId = normalizeId( collection?._id );
	const name = collection?.name || '';

	const close = () => { setRefusal( null ); onClose?.(); };

	const confirm = async () => {
		setBusy( true );
		setRefusal( null );
		try {
			await detachFromParent( collectionId );
			message.success( `${ name || 'This collection' } is no longer part of ${ parent?.name || 'its parent collection' }.` );
			onClose?.();
			onDetached?.();
		}
		catch ( error ) {
			setRefusal( { message: apiErrorMessage( error, 'This collection could not be detached.' ), detail: error?.response?.data || null } );
		}
		finally {
			setBusy( false );
		}
	};

	return (
		<Modal
			open={open}
			title={`Detach from ${ parent?.name || 'its parent collection' }?`}
			okText='Detach'
			okButtonProps={{ danger: true, loading: busy }}
			onOk={confirm}
			onCancel={close}
			destroyOnHidden
		>
			<p>
				<strong>{name}</strong> becomes a collection of its own. It keeps its cards, items,
				settings and sharing, and the time zone it counts its days in now. It stops using { parent?.name || 'the parent collection' }&rsquo;s cards,
				and access given on { parent?.name || 'the parent' } no longer reaches it.
			</p>
			{refusal ? (
				<Alert
					type='warning'
					showIcon
					message={refusal.message}
					description={<DetachBlockers detail={refusal.detail} cardsById={cardsById} collectionId={collectionId} parentName={parent?.name} />}
				/>
			) : null}
		</Modal>
	);
}
